import { FC, useEffect } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Box,
	TextField,
	CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createPricingPlanRequest,
	updatePricingPlanRequest,
	resetCreatePricingPlanSuccess,
	resetUpdatePricingPlanSuccess,
} from 'redux/reducers/euDdp/reducer';
import {
	selectCreatePricingPlanLoading,
	selectCreatePricingPlanSuccess,
	selectUpdatePricingPlanLoading,
	selectUpdatePricingPlanSuccess,
} from 'redux/reducers/euDdp/selectors';
import { TPricingPlanDialogProps } from './types';

const validationSchema = Yup.object({
	monthlyAmount: Yup.number().required('Monthly Amount is required').min(0, 'Must be >= 0'),
	fixedFee: Yup.number().required('Order Fee is required').min(0, 'Must be >= 0'),
	percentageFee: Yup.number()
		.required('Percentage Fee is required')
		.min(0, 'Must be >= 0')
		.max(100, 'Must be <= 100'),
	invoiceName: Yup.string().required('Invoice Name is required').max(255, 'Max 255 characters'),
	description: Yup.string().required('Description is required').max(1000, 'Max 1000 characters'),
});

export const PricingPlanDialog: FC<TPricingPlanDialogProps> = ({
	open,
	onClose,
	pricingPlanToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!pricingPlanToEdit;

	const createLoading = useSelector(selectCreatePricingPlanLoading);
	const createSuccess = useSelector(selectCreatePricingPlanSuccess);
	const updateLoading = useSelector(selectUpdatePricingPlanLoading);
	const updateSuccess = useSelector(selectUpdatePricingPlanSuccess);

	const loading = createLoading || updateLoading;

	const formik = useFormik({
		initialValues: {
			monthlyAmount: pricingPlanToEdit?.monthlyAmount ?? ('' as unknown as number),
			fixedFee: pricingPlanToEdit?.fixedFee ?? ('' as unknown as number),
			percentageFee:
				pricingPlanToEdit?.percentageFee != null
					? pricingPlanToEdit.percentageFee
					: ('' as unknown as number),
			invoiceName: pricingPlanToEdit?.invoiceName ?? '',
			description: pricingPlanToEdit?.description ?? '',
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				monthlyAmount: Number(values.monthlyAmount),
				fixedFee: Number(values.fixedFee),
				percentageFee: Number(values.percentageFee),
				invoiceName: values.invoiceName,
				description: values.description,
			};
			if (isEdit && pricingPlanToEdit) {
				dispatch(updatePricingPlanRequest({ id: pricingPlanToEdit.id, body }));
			} else {
				dispatch(createPricingPlanRequest(body));
			}
		},
	});

	useEffect(() => {
		if (createSuccess || updateSuccess) {
			dispatch(resetCreatePricingPlanSuccess());
			dispatch(resetUpdatePricingPlanSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createSuccess, updateSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isEdit ? 'Edit Pricing Plan' : 'Add New Pricing Plan'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Invoice Name"
						name="invoiceName"
						size="small"
						value={formik.values.invoiceName}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.invoiceName && !!formik.errors.invoiceName}
						helperText={formik.touched.invoiceName && formik.errors.invoiceName}
					/>
					<TextField
						label="Description"
						name="description"
						size="small"
						multiline
						minRows={2}
						value={formik.values.description}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.description && !!formik.errors.description}
						helperText={formik.touched.description && formik.errors.description}
					/>
					<TextField
						label="Monthly Amount (EUR)"
						name="monthlyAmount"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.monthlyAmount}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.monthlyAmount && !!formik.errors.monthlyAmount}
						helperText={formik.touched.monthlyAmount && formik.errors.monthlyAmount}
					/>
					<TextField
						label="Order Fee (EUR)"
						name="fixedFee"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.fixedFee}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.fixedFee && !!formik.errors.fixedFee}
						helperText={formik.touched.fixedFee && formik.errors.fixedFee}
					/>
					<TextField
						label="Percentage Fee (%)"
						name="percentageFee"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.percentageFee}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.percentageFee && !!formik.errors.percentageFee}
						helperText={formik.touched.percentageFee && formik.errors.percentageFee}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
